import React from 'react';
import { Link } from 'react-router-dom';
import Container from '../components/layout/Container';
import { useLanguage } from '../contexts/LanguageContext';
import { getAllDepartments } from '../data/departments';
import './Departments.css';

const Departments = () => {
    const { t, language } = useLanguage();
    const departments = getAllDepartments();

    const deptIcons = {
        'computer-science': 'computer',
        'oil-gas': 'local_gas_station',
        'management': 'corporate_fare',
        'accounting': 'account_balance',
    };

    // Get unique department heads (Diyar is head of both Management & Accounting)
    const deptHeads = [];
    const seenNames = new Set();
    departments.forEach(dept => {
        if (dept.coordinator && !seenNames.has(dept.coordinator.name)) {
            seenNames.add(dept.coordinator.name);
            // Find which departments this coordinator heads
            const headOf = departments
                .filter(d => d.coordinator.name === dept.coordinator.name)
                .map(d => d[language].shortTitle || d[language].title);
            deptHeads.push({
                ...dept.coordinator,
                departments: headOf,
                deptSlug: dept.slug
            });
        }
    });

    return (
        <div className="departments-page">
            {/* Hero Header */}
            <header className="page-header">
                <Container>
                    <span className="section-subtitle">
                        <span className="material-icons" style={{ fontSize: '14px' }}>school</span>
                        ACADEMIC PROGRAMS
                    </span>
                    <h1>
                        Our <span className="text-primary">Departments</span>
                    </h1>
                    <p className="text-muted mt-4" style={{ maxWidth: '600px', margin: '0 auto' }}>
                        Specialized programs designed to prepare you for success in today's competitive job market.
                    </p>
                </Container>
            </header>

            {/* Department Cards Grid */}
            <section className="section">
                <Container>
                    <div className="depts-page-grid">
                        {departments.map((dept, index) => (
                            <div key={dept.id} className={`reveal reveal--up delay-${(index + 1) * 100}`}>
                                <Link to={`/departments/${dept.slug}`} className="dept-full-card">
                                    <div className="dept-full-card__header">
                                        <div className="dept-full-card__icon-wrapper">
                                            <span className="material-icons dept-full-card__icon">
                                                {deptIcons[dept.slug] || 'school'}
                                            </span>
                                        </div>
                                        <span className="dept-full-card__established">
                                            Est. {dept.established}
                                        </span>
                                    </div>
                                    <h3 className="dept-full-card__title">{dept[language].title}</h3>
                                    <p className="dept-full-card__desc">
                                        {dept[language].description.substring(0, 180)}...
                                    </p>

                                    {dept[language].objectives && (
                                        <div className="dept-full-card__objectives">
                                            <h4 className="dept-full-card__objectives-title">Objectives</h4>
                                            <p>{dept[language].objectives.substring(0, 150)}...</p>
                                        </div>
                                    )}

                                    <div className="dept-full-card__footer">
                                        <span className="dept-full-card__link">
                                            {t('common.viewDetails')}
                                            <span className="material-icons" style={{ fontSize: '16px', marginLeft: '4px' }}>arrow_forward</span>
                                        </span>
                                    </div>
                                </Link>
                            </div>
                        ))}
                    </div>
                </Container>
            </section>

            {/* Department Heads Section */}
            <section className="section bg-primary-light">
                <Container>
                    <div className="section-header text-center mb-16 reveal reveal--fade-in">
                        <span className="section-subtitle">
                            <span className="material-icons" style={{ fontSize: '14px' }}>people</span>
                            LEADERSHIP
                        </span>
                        <h2 className="section-title">
                            Department <span className="text-primary">Coordinators</span>
                        </h2>
                        <p className="section-description">
                            Meet the dedicated leaders who guide our academic departments and ensure educational excellence.
                        </p>
                    </div>
                    <div className="dept-heads-grid">
                        {deptHeads.map((head, index) => (
                            <div key={head.name} className={`reveal reveal--up delay-${(index + 1) * 100}`}>
                                <div className="dept-head-card">
                                    <div className="dept-head-card__img-wrapper">
                                        <img src={head.image} alt={head.name} />
                                    </div>
                                    <div className="dept-head-card__info">
                                        <h4 className="dept-head-card__name">{head.name}</h4>
                                        <p className="dept-head-card__role">Department Coordinator</p>
                                        <div className="dept-head-card__depts">
                                            {head.departments.map((deptName, i) => (
                                                <span key={i} className="dept-head-card__dept-badge">{deptName}</span>
                                            ))}
                                        </div>
                                        <div className="dept-head-card__contact">
                                            <a href={`mailto:${head.email}`} className="dept-head-card__contact-item">
                                                <span className="material-icons" style={{ fontSize: '16px' }}>email</span>
                                                {head.email}
                                            </a>
                                            <a href={`tel:${head.phone}`} className="dept-head-card__contact-item">
                                                <span className="material-icons" style={{ fontSize: '16px' }}>phone</span>
                                                {head.phone}
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </Container>
            </section>

            {/* Campus Life Section */}
            <section className="section bg-white">
                <Container>
                    <div className="campus-section">
                        <div className="campus-content reveal reveal--right">
                            <span className="section-subtitle">
                                <span className="material-icons" style={{ fontSize: '14px' }}>diversity_3</span>
                                CAMPUS LIFE
                            </span>
                            <h2 className="section-title">
                                More Than <span className="text-primary">Education</span>
                            </h2>
                            <p className="campus-desc">
                                Da Vinci Institute provides a vibrant campus experience with modern facilities, extracurricular activities, and a supportive community that nurtures both academic and personal growth.
                            </p>
                            <ul className="campus-features">
                                <li>
                                    <span className="material-icons campus-feature-icon">desktop_windows</span>
                                    Modern Computer Labs
                                </li>
                                <li>
                                    <span className="material-icons campus-feature-icon">science</span>
                                    Research Facilities
                                </li>
                                <li>
                                    <span className="material-icons campus-feature-icon">menu_book</span>
                                    Comprehensive Library
                                </li>
                                <li>
                                    <span className="material-icons campus-feature-icon">people</span>
                                    Student Clubs
                                </li>
                            </ul>
                        </div>
                        <div className="campus-visual reveal reveal--left delay-200">
                            <div className="campus-visual-card">
                                <span className="material-icons" style={{ fontSize: '64px', color: 'var(--color-primary-500)', opacity: 0.3 }}>apartment</span>
                            </div>
                        </div>
                    </div>
                </Container>
            </section>
        </div>
    );
};

export default Departments;
